import { Dropdown, EntityChip, Label, TextInput, Tooltip } from '@silverfort/sf-components';
import { FC, ReactNode } from 'react';
import moment from 'moment/moment';
import { CLASSIFICATION_OPTIONS } from '../../ServiceAccountsConsts';
import { useServiceAccountContext } from '../../contexts/ServiceAccountContext';
import { SaOwners } from './SaOwners/SaOwners';
import useHasPermissions from '../../../../../hooks/useHasPermissions';
import { Pages, Roles } from '../../../../../context/contexts/UserContext/permissionsTypes';

interface UserDetailProps {
    title: string;
    value: string | ReactNode;
    isLoading: boolean;
}

const UserDetail: FC<UserDetailProps> = ({ title, value, isLoading }) => (
    <div className="flex flex-1 flex-col text-13 font-normal gap-2 px-3">
        <span className="text-sf-gray-500">{title}</span>
        {isLoading ? (
            <div className="animate-pulse h-4 w-full rounded bg-sf-gray-50"></div>
        ) : (
            <span className="text-sf-gray-800">{value}</span>
        )}
    </div>
);

const PropertiesTab: FC = () => {
    const { updatedServiceAccount, updateServiceAccountData } = useServiceAccountContext();
    const isEditor = useHasPermissions({ permission: Pages.SERVICE_ACCOUNTS, role: Roles.VIEWER });

    return (
        <div className="flex flex-col overflow-y-auto h-[calc(100vh-350px)] gap-8">
            <div className="flex flex-col divide-y-2 divide-dashed">
                <div className="flex py-3">
                    <UserDetail
                        title="UPN"
                        value={updatedServiceAccount?.identifier || updatedServiceAccount?.display_name}
                        isLoading={!updatedServiceAccount}
                    />
                    <UserDetail title="Identity store" value="Active Directory" isLoading={!updatedServiceAccount} />
                </div>
                <div className="flex py-3">
                    <UserDetail
                        title="Domain"
                        value={updatedServiceAccount?.domain_name}
                        isLoading={!updatedServiceAccount}
                    />
                    <UserDetail
                        title="Last seen"
                        value={
                            updatedServiceAccount?.last_use
                                ? moment(updatedServiceAccount.last_use).format('MMM DD, YYYY')
                                : 'No data'
                        }
                        isLoading={!updatedServiceAccount}
                    />
                </div>
                <div className="flex py-3">
                    <UserDetail
                        title="Last rotation"
                        value={
                            updatedServiceAccount?.pwd_last_set
                                ? moment(updatedServiceAccount.pwd_last_set * 1000).format('MMM DD, YYYY')
                                : 'No data'
                        }
                        isLoading={!updatedServiceAccount}
                    />
                    <UserDetail
                        title={
                            updatedServiceAccount
                                ? `Group membership (${updatedServiceAccount.groups.length})`
                                : 'Group membership'
                        }
                        value={
                            <span className="flex flex-wrap gap-2">
                                {updatedServiceAccount?.groups.map((group) => (
                                    <EntityChip text={group.name} key={group.guid} />
                                ))}
                            </span>
                        }
                        isLoading={!updatedServiceAccount}
                    />
                </div>
            </div>
            <div className="rounded-lg flex flex-col py-6 gap-4 border">
                <div className="px-6 pb-4 border-b">
                    <SaOwners />
                </div>

                <div className="px-6 flex gap-4">
                    <div className="flex gap-6">
                        <Label text="Category" className="min-w-16 self-center text-sf-gray-800" />
                        {updatedServiceAccount ? (
                            <Tooltip
                                title={
                                    !!updatedServiceAccount.remove_reason
                                        ? 'Category changes are not available for Discarded Accounts'
                                        : ''
                                }>
                                <Dropdown
                                    disabled={!!updatedServiceAccount.remove_reason || !isEditor}
                                    variant="trigger"
                                    className="border"
                                    selectedItems={updatedServiceAccount?.type_resolved}
                                    items={CLASSIFICATION_OPTIONS}
                                    onChange={(val) => {
                                        const category = Array.isArray(val) ? val[0] : val;
                                        updateServiceAccountData('type_resolved', category);
                                    }}
                                    size={'small'}
                                    text="Select category"
                                />
                            </Tooltip>
                        ) : (
                            <div className="animate-pulse h-4 w-full rounded bg-sf-gray-50"></div>
                        )}
                    </div>
                    <div className="flex flex-1 gap-6">
                        <Label text="Application" className="w-16 self-center text-sf-gray-800" />
                        {updatedServiceAccount ? (
                            <TextInput
                                disabled={!isEditor}
                                onChange={(val) => {
                                    updateServiceAccountData('application', val);
                                }}
                                defaultValue={updatedServiceAccount.application}
                                className="w-full h-8"
                                wrapperClassName="w-full"
                                placeholder="Add Application"
                            />
                        ) : (
                            <div className="animate-pulse h-4 w-full rounded bg-sf-gray-50"></div>
                        )}
                    </div>
                </div>
                <div className="px-6 flex gap-6">
                    <Label text="Comment" className="min-w-16 self-center text-sf-gray-800" />
                    {updatedServiceAccount ? (
                        <TextInput
                            disabled={!isEditor}
                            onChange={(val) => {
                                updateServiceAccountData('comment', val);
                            }}
                            defaultValue={updatedServiceAccount.comment}
                            className="w-full h-8"
                            wrapperClassName="w-full"
                            placeholder="Add Comment"
                        />
                    ) : (
                        <div className="animate-pulse h-4 w-full rounded bg-sf-gray-50"></div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PropertiesTab;
